/*
 * AI search client. Reads window.AISearch (set by snippets/ai-search-config.liquid).
 * Exposes window.AISearchClient with query() + render helpers. Fail-soft: query()
 * returns null on any non-OK/error so callers can fall back to native search.
 */
(function () {
  const cfg = window.AISearch || {};

  function ready() {
    return !!(cfg.enabled && cfg.url && cfg.tenant);
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function formatPrice(price) {
    if (price == null) return '';
    var n = Number(price);
    var whole = n % 1 === 0;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: cfg.currency || 'USD',
        minimumFractionDigits: whole ? 0 : 2,
        maximumFractionDigits: 2,
      }).format(n);
    } catch (e) {
      return '$' + (whole ? n.toFixed(0) : n.toFixed(2));
    }
  }

  async function query(term, opts) {
    opts = opts || {};
    if (!ready()) return null;
    const t = (term || '').trim();
    if (t.length < 2) return { query: t, products: [], articles: [], bestMatch: null };
    const base = cfg.url.replace(/\/+$/, '');
    const url = base + '/api/search?tenant=' + encodeURIComponent(cfg.tenant) + '&q=' + encodeURIComponent(t);
    try {
      const res = await fetch(url, { signal: opts.signal });
      if (!res.ok) return null;
      let data = await res.json();
      // Cross-store links + merchant pins (ai-search-shape.js), then drop
      // anything the live store says is sold out or gone (ai-search-stock.js).
      if (window.AISearchShape) data = window.AISearchShape.shapeResult(data, cfg.pins);
      if (window.AISearchStock) data = await window.AISearchStock.verify(data);
      return data;
    } catch (e) {
      if (e && e.name === 'AbortError') throw e;
      return null;
    }
  }

  // Markup for the header predictive dropdown. Reuses Dawn's predictive-search
  // classes + ids so the inherited keyboard navigation keeps working. Compact:
  // top products in a 2-col grid, then a couple of articles — the full result
  // set stays one Enter away on /search.
  const PRED_PRODUCTS = 4;
  const PRED_ARTICLES = 2;
  function renderPredictive(data, term) {
    const products = (data.products || []).slice(0, PRED_PRODUCTS);
    const articles = (data.articles || []).slice(0, PRED_ARTICLES);
    const total = (data.products || []).length + (data.articles || []).length;
    let html = '<div id="predictive-search-results" role="listbox">';

    if (total > 0) {
      html += '<div id="predictive-search-results-groups-wrapper" class="predictive-search__results-groups-wrapper ai-predictive-groups">';

      if (products.length) {
        html += '<div class="predictive-search__result-group"><div>';
        html += '<h2 class="predictive-search__heading text-body caption-with-letter-spacing">' + escapeHtml(cfg.productsLabel || 'Products') + '</h2>';
        html += '<ul class="predictive-search__results-list list-unstyled ai-predictive-products" role="group">';
        products.forEach(function (p, i) {
          const img = p.image
            ? '<img class="predictive-search__image" src="' + escapeHtml(p.image) + '" alt="' + escapeHtml(p.title) + '" width="56" height="56" loading="lazy">'
            : '';
          const price = p.price != null
            ? '<div class="predictive-search__item-price h5">' + escapeHtml(formatPrice(p.price)) + '</div>'
            : '';
          html +=
            '<li id="predictive-search-option-product-' + (i + 1) + '" class="predictive-search__list-item" role="option" aria-selected="false">' +
            '<a href="' + escapeHtml(p.url) + '" class="predictive-search__item predictive-search__item--link-with-thumbnail link link--text" tabindex="-1"' +
            ' data-ai-result data-ai-item="' + escapeHtml(p.slug) + '" data-ai-kind="product" data-ai-position="' + (i + 1) + '" data-ai-surface="header">' +
            img +
            '<div class="predictive-search__item-content">' +
            '<p class="predictive-search__item-heading h5">' + escapeHtml(p.title) + '</p>' +
            price +
            '</div></a></li>';
        });
        html += '</ul></div></div>';
      }

      if (articles.length) {
        html += '<div class="predictive-search__result-group"><div class="predictive-search__pages-wrapper">';
        html += '<h2 class="predictive-search__heading text-body caption-with-letter-spacing">Guides &amp; articles</h2>';
        html += '<ul class="predictive-search__results-list list-unstyled" role="group">';
        articles.forEach(function (a, i) {
          html +=
            '<li id="predictive-search-option-article-' + (i + 1) + '" class="predictive-search__list-item" role="option" aria-selected="false">' +
            '<a href="' + escapeHtml(a.url) + '" class="predictive-search__item link link--text" tabindex="-1"' +
            ' data-ai-result data-ai-item="' + escapeHtml(a.slug) + '" data-ai-kind="article" data-ai-position="' + (i + 1) + '" data-ai-surface="header">' +
            '<div class="predictive-search__item-content predictive-search__item-content--centered">' +
            '<p class="predictive-search__item-heading h5">' + escapeHtml(a.title) + '</p>' +
            '</div></a></li>';
        });
        html += '</ul></div></div>';
      }

      html += '</div>'; // groups-wrapper
    }

    if (data.didYouMean) {
      html += '<div class="ai-didyoumean" role="option" aria-selected="false">' +
        '<a class="predictive-search__item link link--text" href="/search?q=' + encodeURIComponent(data.didYouMean) + '">' +
        'Did you mean &ldquo;<strong>' + escapeHtml(data.didYouMean) + '</strong>&rdquo;?</a></div>';
    }
    const searchFor = (cfg.searchForLabel || 'Search for "__TERMS__"').replace('__TERMS__', escapeHtml(term));
    html +=
      '<div id="predictive-search-option-search-keywords" class="predictive-search__search-for-button">' +
      '<button class="predictive-search__item predictive-search__item--term link link--text h5 animate-arrow" tabindex="-1" role="option" aria-selected="false">' +
      '<span data-predictive-search-search-for-text>' + searchFor + '</span>' +
      '</button></div>';

    html += '</div>'; // results

    const countText = total === 0 ? 'No results' : total + ' results';
    html += '<span class="hidden" data-predictive-search-live-region-count-value>' + countText + '</span>';
    return html;
  }

  // Star rating row (Dawn-style width trick for fractional ratings).
  function renderStars(rating, count) {
    if (!rating || !count) return '';
    var pct = Math.max(0, Math.min(100, (Number(rating) / 5) * 100));
    return '<div class="ai-stars-row" role="img" aria-label="' + escapeHtml(String(rating)) + ' out of 5 stars, ' + count + ' reviews">' +
      '<span class="ai-stars"><span class="ai-stars__bg">★★★★★</span>' +
      '<span class="ai-stars__fg" style="width:' + pct.toFixed(1) + '%">★★★★★</span></span>' +
      '<span class="ai-stars__count">(' + count + ')</span></div>';
  }

  // A single result card for the /search + collection grids — styled to match
  // the theme's category cards (card color scheme, stars, add-to-cart).
  function renderCard(item, surface, position) {
    const isProduct = item.kind === 'product';
    const attrs = ' data-ai-result data-ai-item="' + escapeHtml(item.slug) + '" data-ai-kind="' + (isProduct ? 'product' : 'article') + '"' +
      ' data-ai-position="' + (position || 0) + '" data-ai-surface="' + escapeHtml(surface || '') + '"';
    const img = item.image
      ? '<div class="ai-search-card__media"><img src="' + escapeHtml(item.image) + '" alt="' + escapeHtml(item.title) + '" loading="lazy"></div>'
      : '';
    if (!isProduct) {
      return (
        '<li class="grid__item ai-search-card">' +
        '<a class="ai-search-card__link" href="' + escapeHtml(item.url) + '"' + attrs + '>' +
        img +
        '<div class="ai-search-card__info"><span class="ai-search-card__badge">' + escapeHtml(cfg.pagesLabel || 'Article') + '</span>' +
        '<h3 class="ai-search-card__title h5">' + escapeHtml(item.title) + '</h3>' +
        '</div></a></li>'
      );
    }
    const scheme = 'color-' + (cfg.cardScheme || 'scheme-1') + ' gradient';
    const price = item.price != null
      ? '<div class="ai-search-card__price">' + (item.hasOptions ? 'From ' : '') + escapeHtml(formatPrice(item.price)) + '</div>'
      : '';
    let button;
    if (item.available === false) {
      button = '<button class="ai-search-card__btn" disabled>Sold out</button>';
    } else if (item.hasOptions || !item.variantId) {
      button = '<a class="ai-search-card__btn" href="' + escapeHtml(item.url) + '"' + attrs + '>Choose options</a>';
    } else {
      button = '<button class="ai-search-card__btn" data-ai-atc="' + escapeHtml(item.variantId) + '"' +
        ' data-ai-handle="' + escapeHtml(item.slug) + '" data-ai-btn-position="' + (position || 0) + '"' +
        ' data-ai-btn-surface="' + escapeHtml(surface || '') + '">Add to cart</button>';
    }
    return (
      '<li class="grid__item ai-search-card ai-search-card--product">' +
      '<div class="ai-search-card__inner ' + scheme + '">' +
      '<a class="ai-search-card__link" href="' + escapeHtml(item.url) + '"' + attrs + '>' +
      img +
      '<div class="ai-search-card__info">' +
      renderStars(item.rating, item.ratingCount) +
      '<h3 class="ai-search-card__title h5">' + escapeHtml(item.title) + '</h3>' + price +
      '</div></a>' +
      '<div class="ai-search-card__actions">' + button + '</div>' +
      '</div></li>'
    );
  }

  // Add-to-cart from result cards: Shopify AJAX add + header bubble refresh.
  // The analytics fetch-wrap auto-logs the attributed cart event.
  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest ? e.target.closest('[data-ai-atc]') : null;
    if (!btn || btn.disabled) return;
    e.preventDefault();
    var orig = btn.textContent;
    try {
      if (window.AISearchAnalytics) {
        window.AISearchAnalytics.logClick(btn.getAttribute('data-ai-handle'), 'product',
          Number(btn.getAttribute('data-ai-btn-position')) || null, btn.getAttribute('data-ai-btn-surface') || '');
      }
    } catch (err) {}
    btn.disabled = true;
    btn.textContent = 'Adding…';
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: Number(btn.getAttribute('data-ai-atc')), quantity: 1 }),
    })
      .then(function (r) { if (!r.ok) throw new Error('add failed'); return r.json(); })
      .then(function () {
        btn.textContent = 'Added ✓';
        fetch('/?sections=cart-icon-bubble')
          .then(function (r) { return r.json(); })
          .then(function (j) {
            var el = document.getElementById('cart-icon-bubble');
            if (el && j && j['cart-icon-bubble']) {
              var d = document.createElement('div');
              d.innerHTML = j['cart-icon-bubble'];
              var n = d.querySelector('#cart-icon-bubble') || d.firstElementChild;
              if (n) el.innerHTML = n.innerHTML;
            }
          })
          .catch(function () {});
        setTimeout(function () { btn.textContent = orig; btn.disabled = false; }, 2000);
      })
      .catch(function () { btn.textContent = orig; btn.disabled = false; });
  }, true);

  // Results for the /search + collection pages. Products render in the grid;
  // articles get their own compact section below — image-less text cards mixed
  // into a product grid float awkwardly next to tall product cards.
  function renderGrid(data, surface) {
    const products = data.products || [];
    const articles = data.articles || [];
    let html = '<div class="template-search__results collection page-width" id="product-grid">';
    if (products.length) {
      html += '<ul class="grid product-grid grid--2-col-tablet-down grid--4-col-desktop ai-search-grid" role="list">' +
        products.map(function (item, i) { return renderCard(item, surface || '', i + 1); }).join('') + '</ul>';
    }
    if (articles.length) {
      html += '<div class="ai-articles"><h3 class="ai-discovery__title">Guides &amp; articles</h3>' +
        '<ul class="ai-articles__list" role="list">' +
        articles.map(function (a, i) {
          var snip = a.snippet ? '<p class="ai-article-card__snippet">' + escapeHtml(a.snippet) + '</p>' : '';
          return '<li><a class="ai-article-card" href="' + escapeHtml(a.url) + '"' +
            ' data-ai-result data-ai-item="' + escapeHtml(a.slug) + '" data-ai-kind="article"' +
            ' data-ai-position="' + (products.length + i + 1) + '" data-ai-surface="' + escapeHtml(surface || '') + '">' +
            '<h3 class="ai-article-card__title">' + escapeHtml(a.title) + '</h3>' + snip + '</a></li>';
        }).join('') + '</ul></div>';
    }
    html += '</div>';
    return html;
  }

  // Top queries from the analytics service: terms, or {query, count} when
  // withCounts (the panel's popular-searches gate). Fail-soft → [].
  async function trending(limit, withCounts) {
    if (!ready()) return [];
    try {
      const base = cfg.url.replace(/\/+$/, '');
      const url = base + '/api/trending?tenant=' + encodeURIComponent(cfg.tenant) + '&limit=' + (limit || 8);
      const res = await fetch(url);
      if (!res.ok) return [];
      const data = await res.json();
      return (data.queries || []).map(function (q) { return withCounts ? q : q.query; });
    } catch (e) {
      return [];
    }
  }

  // ---- Search panel support (assets/ai-search-panel.js) ----------------------

  var panel; // parsed [data-ais-data] JSON from snippets/ai-search-panel.liquid
  function panelData() {
    if (panel === undefined) {
      var el = document.querySelector('[data-ais-data]');
      try { panel = el ? JSON.parse(el.textContent) : null; } catch (e) { panel = null; }
    }
    return panel;
  }

  function aiAttrs(slug, kind, position, surface) {
    return ' data-ai-result data-ai-item="' + escapeHtml(slug) + '" data-ai-kind="' + kind + '"' +
      ' data-ai-position="' + position + '" data-ai-surface="' + escapeHtml(surface) + '"';
  }

  // The theme's own markup for one product via an alternate product view
  // (templates/product.<view>.liquid). '' = the store rendered nothing (sold out
  // or hidden) so the product is dropped; null = fetch failed, lean card instead.
  var views = {};
  function fetchView(handle, view) {
    var key = view + '|' + handle;
    if (!views[key]) {
      views[key] = fetch('/products/' + encodeURIComponent(handle) + '?view=' + view)
        .then(function (r) { return r.ok ? r.text() : (r.status === 404 ? '' : null); })
        .then(function (html) { return html == null ? null : html.trim(); })
        .catch(function () { return null; });
    }
    return views[key];
  }

  function fallbackPanelCard(item) {
    return '<a class="ais-card" href="' + escapeHtml(item.url) + '"><span class="ais-card__img">' +
      (item.image ? '<img src="' + escapeHtml(item.image) + '" alt="' + escapeHtml(item.title) + '" loading="lazy">' : '') +
      '</span><span class="ais-card__title">' + escapeHtml(item.title) + '</span>' +
      (item.price != null ? '<span class="ais-card__price">' + (item.hasOptions ? 'From ' : '') + escapeHtml(formatPrice(item.price)) + '</span>' : '') + '</a>';
  }

  // Products through a theme view, in AI order, dropping the ones the store
  // renders empty. Resolves to { html, count }.
  function renderProducts(items, view, surface) {
    return Promise.all(items.map(function (item) { return fetchView(item.slug, view); })).then(function (marks) {
      var out = [];
      marks.forEach(function (html, i) {
        if (html === '') return;
        var card = html == null ? fallbackPanelCard(items[i]) : html;
        out.push(card.replace(/^<([a-zA-Z0-9-]+)/, '<$1' + aiAttrs(items[i].slug, 'product', out.length + 1, surface)));
      });
      return { html: out.join(''), count: out.length };
    });
  }

  // Recent 5-star reviews (zero-result social proof). Optional handles filter
  // scopes to specific products — "reviews relevant to this search". Fail-soft → [].
  async function reviewsHighlights(limit, handles, q) {
    if (!ready()) return [];
    try {
      const base = cfg.url.replace(/\/+$/, '');
      let url = base + '/api/reviews-highlights?tenant=' + encodeURIComponent(cfg.tenant) + '&limit=' + (limit || 4);
      if (handles && handles.length) url += '&products=' + encodeURIComponent(handles.slice(0, 10).join(','));
      if (q) url += '&q=' + encodeURIComponent(q);
      const res = await fetch(url);
      if (!res.ok) return [];
      const data = await res.json();
      return data.reviews || [];
    } catch (e) {
      return [];
    }
  }

  // Compact review cards for the predictive dropdown (few-result queries).
  function renderPredictiveReviews(reviews) {
    if (!reviews || !reviews.length) return '';
    return '<div class="ai-reviews ai-reviews--predictive">' +
      '<h3 class="ai-discovery__title">Loved by shoppers</h3>' +
      reviews.map(function (r) {
        var img = r.product.image
          ? '<img class="ai-review-card__img" src="' + escapeHtml(r.product.image) + '" alt="' + escapeHtml(r.product.title) + '" loading="lazy">'
          : '';
        return '<a class="ai-review-card" href="' + escapeHtml(r.product.url) + '"' +
          ' data-ai-result data-ai-item="' + escapeHtml(r.product.handle) + '" data-ai-kind="review" data-ai-surface="header">' +
          img +
          '<div class="ai-review-card__content">' +
          '<div class="ai-review-card__product-title">' + escapeHtml(r.product.title) + '</div>' +
          '<div class="ai-review-card__stars" aria-label="5 out of 5 stars">★★★★★</div>' +
          '<p class="ai-review-card__body">&ldquo;' + escapeHtml(r.body) + '&rdquo;</p>' +
          '<div class="ai-review-card__meta">' + escapeHtml(r.reviewer) + (r.verified ? ' · Verified buyer' : '') + '</div>' +
          '</div></a>';
      }).join('') + '</div>';
  }

  // A "no results → try these" block: product suggestions + 5-star UGC + Recent/Popular chips.
  function renderDiscovery(query, popular, recent, items, reviews, didYouMean) {
    function chips(list) {
      return list.map(function (q) {
        return '<a class="ai-discovery__chip" href="/search?q=' + encodeURIComponent(q) + '">' + escapeHtml(q) + '</a>';
      }).join('');
    }
    let html = '<div class="ai-discovery page-width">';
    if (query) {
      html += '<p class="ai-discovery__lead">No exact match for &ldquo;' + escapeHtml(query) + '&rdquo;.' +
        (didYouMean
          ? ' Did you mean <a class="ai-didyoumean__link" href="/search?q=' + encodeURIComponent(didYouMean) + '"><strong>' + escapeHtml(didYouMean) + '</strong></a>?'
          : '') + '</p>';
    }
    // Review-led product recommendations FIRST: product image + title headline,
    // the recent 5-star review as the kicker to research further.
    if (reviews && reviews.length) {
      html += '<div class="ai-discovery__group"><h3 class="ai-discovery__title">Loved by shoppers right now</h3>' +
        '<div class="ai-reviews">' +
        reviews.map(function (r) {
          var img = r.product.image
            ? '<img class="ai-review-card__img" src="' + escapeHtml(r.product.image) + '" alt="' + escapeHtml(r.product.title) + '" loading="lazy">'
            : '';
          return '<a class="ai-review-card" href="' + escapeHtml(r.product.url) + '"' +
            ' data-ai-result data-ai-item="' + escapeHtml(r.product.handle) + '" data-ai-kind="review" data-ai-surface="search">' +
            img +
            '<div class="ai-review-card__content">' +
            '<div class="ai-review-card__product-title">' + escapeHtml(r.product.title) + '</div>' +
            '<div class="ai-review-card__stars" aria-label="5 out of 5 stars">★★★★★</div>' +
            '<p class="ai-review-card__body">&ldquo;' + escapeHtml(r.body) + '&rdquo;</p>' +
            '<div class="ai-review-card__meta">' + escapeHtml(r.reviewer) + (r.verified ? ' · Verified buyer' : '') + '</div>' +
            '</div></a>';
        }).join('') + '</div></div>';
    }
    if (items && items.length) {
      html += '<div class="ai-discovery__group"><h3 class="ai-discovery__title">Shoppers looking for this often choose</h3>' +
        '<ul class="grid product-grid grid--2-col-tablet-down grid--4-col-desktop ai-search-grid ai-discovery__products" role="list">' +
        items.map(function (it, i) { return renderCard(it, 'search', i + 1); }).join('') + '</ul></div>';
    }
    // One compact, deduped chip row (recent first, then popular; query excluded).
    var merged = [];
    var seenChip = {};
    (recent || []).concat(popular || []).forEach(function (q2) {
      var k = String(q2 || '').toLowerCase();
      if (!k || seenChip[k] || (query && k === String(query).toLowerCase())) return;
      seenChip[k] = true;
      merged.push(q2);
    });
    merged = merged.slice(0, 8);
    if (merged.length) {
      html += '<div class="ai-discovery__group"><h3 class="ai-discovery__title">Try a different search</h3>' +
              '<div class="ai-discovery__chips">' + chips(merged) + '</div></div>';
    }
    html += '</div>';
    return html;
  }

  window.AISearchClient = {
    cfg: cfg,
    panelData: panelData,
    aiAttrs: aiAttrs,
    renderProducts: renderProducts,
    ready: ready,
    query: query,
    trending: trending,
    reviewsHighlights: reviewsHighlights,
    escapeHtml: escapeHtml,
    formatPrice: formatPrice,
    renderPredictive: renderPredictive,
    renderCard: renderCard,
    renderGrid: renderGrid,
    renderDiscovery: renderDiscovery,
    renderPredictiveReviews: renderPredictiveReviews,
  };
})();
